import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { mediaAPI } from '../../lib/api';
import { UnifiedContentEditor, SidebarCard } from '../../components/admin/UnifiedContentEditor';
import { CitationPicker } from '../../components/admin/CitationPicker';
import { ReferencesManager } from '../../components/admin/ReferencesManager';

export default function MediaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const editorRef = useRef(null);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  
  // Core content
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  
  // Metadata specific to media
  const [source, setSource] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Citations
  const [citations, setCitations] = useState([]);
  const [showCitationPicker, setShowCitationPicker] = useState(false);

  // Fetch existing media data if editing
  useEffect(() => {
    const fetchData = async () => {
      if (isEditing) {
        try {
          const response = await mediaAPI.getOne(id);
          const data = response.data;
          setTitle(data.title || '');
          setDescription(data.description || '');
          setContent(data.content || '');
          setFeaturedImage(data.image || '');
          setSource(data.source || '');
          setArticleUrl(data.url || '');
          setDate(data.date ? data.date.split('T')[0] : new Date().toISOString().split('T')[0]);
          setCitations(data.citations || []);
        } catch (error) {
          toast.error('Failed to load media data');
          navigate('/admin/media');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [id, isEditing, navigate]);

  // Handle selecting a citation from the picker
  const handleSelectCitation = (citationIndex) => {
    if (editorRef.current) {
      editorRef.current.insertCitation(citationIndex);
    }
  };

  // Handle adding a new citation
  const handleAddCitation = (citationHtml) => {
    setCitations([...citations, citationHtml]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !source.trim()) {
      toast.error('Please fill in title and source');
      return;
    }

    setSaving(true);

    const formData = {
      title,
      source,
      url: articleUrl,
      date,
      description,
      content,
      image: featuredImage,
      citations
    };

    try {
      if (isEditing) {
        await mediaAPI.update(id, formData);
        toast.success('Media coverage updated successfully');
      } else {
        await mediaAPI.create(formData);
        toast.success('Media coverage added successfully');
      }
      navigate('/admin/media');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'add'} media`);
    } finally {
      setSaving(false);
    }
  };

  // Loading state for edit mode
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading media data...</div>
      </div>
    );
  }

  // Sidebar content for media coverage
  const sidebarContent = (
    <>
      {/* Source & Date */}
      <SidebarCard title="Publication Details">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Source *</Label>
            <Input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g., Times of India"
              className="text-sm"
              required
              data-testid="media-source"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Publication Date *</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="text-sm"
              required
              data-testid="media-date"
            />
          </div>
        </div>
      </SidebarCard>

      {/* Article URL */}
      <SidebarCard title="External Link">
        <div className="space-y-2">
          <Label className="text-sm">Article URL</Label>
          <Input
            value={articleUrl}
            onChange={(e) => setArticleUrl(e.target.value)}
            placeholder="https://..."
            className="text-sm"
            data-testid="media-url"
          />
          <p className="text-xs text-muted-foreground">
            Link to the original article or media coverage.
          </p>
        </div>
      </SidebarCard>

      {/* References Manager */}
      <SidebarCard title="">
        <ReferencesManager
          citations={citations}
          onCitationsChange={setCitations}
          maxHeight="150px"
        />
      </SidebarCard>
    </>
  );

  return (
    <>
      <UnifiedContentEditor
        ref={editorRef}
        contentType="media"
        pageTitle={isEditing ? "Edit Media Coverage" : "Add Media Coverage"}
        
        // Core content
        title={title}
        onTitleChange={setTitle}
        titlePlaceholder="Enter article or coverage title..."
        
        // Description
        showExcerpt={true}
        excerpt={description}
        onExcerptChange={setDescription}
        excerptPlaceholder="Brief summary of the coverage..."
        excerptHelp="A short description of what this media coverage is about."
        
        // Content (optional extended coverage notes)
        content={content}
        onContentChange={setContent}
        contentPlaceholder="Add any additional notes or extended content about this media coverage..."
        
        // Featured image
        featuredImage={featuredImage}
        onFeaturedImageChange={setFeaturedImage}
        
        // Actions
        onBack={() => navigate('/admin/media')}
        onSubmit={handleSubmit}
        saving={saving}
        isEditing={isEditing}
        submitLabel={isEditing ? "Update Media Coverage" : "Add Media Coverage"}
        
        // Sidebar
        sidebarContent={sidebarContent}
        
        // Citation support
        citations={citations}
        onInsertCitation={() => setShowCitationPicker(true)}
        showCitationButton={true}
      />

      {/* Citation Picker Modal */}
      <CitationPicker
        open={showCitationPicker}
        onOpenChange={setShowCitationPicker}
        citations={citations}
        onSelectCitation={handleSelectCitation}
        onAddCitation={handleAddCitation}
      />
    </>
  );
}
